/* SPEC-139 — the frontend half of the single page/tab source.
 *
 * The structure lives in ONE file, config/view-registry.json, which the
 * backend (backend/view_registry.py) reads too. This module types it and
 * gives the nav shells (Sidebar + the collapsed-sidebar SectionNavBar) their
 * shared lookups so there is exactly one place that turns a route into its
 * tab groups. Presentation (icons, tooltips, status, top-level grouping)
 * stays in Sidebar.tsx — this module is structure only. */
import registry from '@/config/view-registry.json';

export interface RegistryTab {
  key: string;
  label: string;
  advanced?: boolean;
}

export interface RegistryGroup {
  /** '' for flat pages — the nav renders those without a group header. */
  label: string;
  tabs: RegistryTab[];
}

export interface RegistryPage {
  path: string;
  label: string;
  groups?: RegistryGroup[];
  /** SPEC-147: the All/Produced/Imported top-bar filter is only shown on pages
   *  that actually consume it. 'wired' = the toggle changes what's on screen
   *  end-to-end (only /demand-planning today); 'planned' = documented as a
   *  candidate but NOT yet functional, so the toggle stays hidden (no decorative
   *  control). Absent = not applicable. */
  productFilter?: 'wired' | 'planned';
}

export const VIEW_REGISTRY = registry as RegistryPage[];

/** The registry page owning a given route — mirrors Sidebar's active test
 *  (exact for '/', prefix otherwise). */
export function findRegistryPage(pathname: string): RegistryPage | undefined {
  return VIEW_REGISTRY.find((p) =>
    p.path === '/' ? pathname === '/' : pathname.startsWith(p.path),
  );
}

/** Flat allowed-key list for a page after workspace + user filtering
 *  (SPEC-057). undefined = page has no sub-tabs. */
/** SPEC-231: the three retired buying tabs collapsed into 'psr-console'. Existing
 *  role/workspace templates (stored in the DB) still grant the OLD keys, so a view
 *  that could see any of them must see the survivor — otherwise a scoped/demo user
 *  would lose the console. Expand a legacy grant to include 'psr-console' before
 *  filtering, so no DB migration is needed and no role loses access. */
const RETIRED_PSR_TABS = ['demand-schedule', 'replenishment', 'netting'];
function withPsrAlias(allow: string[]): string[] {
  return allow.some((k) => RETIRED_PSR_TABS.includes(k)) && !allow.includes('psr-console')
    ? [...allow, 'psr-console']
    : allow;
}

export function visibleTabKeys(
  page: RegistryPage | undefined,
  workspaceTabs?: string[],
  userTabs?: string[],
): string[] | undefined {
  if (!page?.groups) return undefined;
  let keys = page.groups.flatMap((g) => g.tabs.map((t) => t.key));
  if (workspaceTabs) keys = keys.filter((k) => withPsrAlias(workspaceTabs).includes(k));
  if (userTabs) keys = keys.filter((k) => withPsrAlias(userTabs).includes(k));
  return keys;
}
